import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';
import { useThemeContext } from '../../../utils/providers/theme-context';

type InfoBannerProps = {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
};

export const InfoBanner = ({
  title,
  children,
  defaultExpanded = false
}: InfoBannerProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const borderColor = pick('rgb(191 219 254)', 'rgb(30 64 175)'); // blue-200 / blue-800
  const bgColor = pick('rgb(239 246 255)', 'rgb(30 58 138)'); // blue-50 / blue-900
  const hoverBgColor = pick('rgb(219 234 254)', 'rgb(29 78 216)'); // blue-100 / blue-700
  const iconColor = pick('rgb(37 99 235)', 'rgb(96 165 250)'); // blue-600 / blue-400
  const textColor = pick('rgb(30 58 138)', 'rgb(191 219 254)'); // blue-900 / blue-200

  return (
    <div
      className="mb-4 border rounded-xl overflow-hidden"
      style={{
        borderColor: borderColor,
        backgroundColor: bgColor
      }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 transition-colors"
        style={{ backgroundColor: bgColor }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = hoverBgColor;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = bgColor;
        }}
      >
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4" style={{ color: iconColor }} />
          <span className="text-sm font-medium" style={{ color: textColor }}>
            {title}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5" style={{ color: iconColor }} />
        ) : (
          <ChevronDown className="h-5 w-5" style={{ color: iconColor }} />
        )}
      </button>

      {isExpanded && (
        <div
          className="px-4 pb-4 text-sm space-y-2"
          style={{ color: textColor }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
